import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Card, DataBoundaryBar, EmptyState, PageHeader } from '@/components/ui'

/**
 * R41: the cross-partner view, visible to Ki Tua o Matariki only. It reads the
 * same view partners read; the difference is entirely in what the policies let
 * each caller see, so there is no second query path to keep in step.
 */
export function AggregateReporting() {
  const { data, isLoading } = useQuery({
    queryKey: ['aggregate-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('evaluation_submission_counts')
        .select('organisation, form_title, submitted_count, draft_count, last_submitted_at')
        .order('organisation')
      if (error) throw error
      return data
    },
  })

  const total = (data ?? []).reduce((sum, row) => sum + Number(row.submitted_count ?? 0), 0)

  return (
    <>
      <PageHeader title="Aggregate reporting">
        The longer impact story across every partner, for reporting to funders and the board.
      </PageHeader>

      <DataBoundaryBar />

      {isLoading && <p className="text-sm text-inksoft">Loading…</p>}

      {!isLoading && (data ?? []).length === 0 ? (
        <EmptyState
          title="No submissions yet"
          body="Once partners start recording sessions, the aggregate picture builds here."
        />
      ) : (
        <>
          <Card className="mb-6">
            <p className="ref">Submitted entries, all partners</p>
            <p className="mt-2 font-display text-4xl text-pounamu">{total}</p>
          </Card>
          <div className="overflow-x-auto rounded-md border border-line bg-surface">
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-inksoft">
                <tr>
                  <th className="px-4 py-2 font-medium">Organisation</th>
                  <th className="px-4 py-2 font-medium">Form</th>
                  <th className="px-4 py-2 font-medium">Submitted</th>
                  <th className="px-4 py-2 font-medium">Draft</th>
                  <th className="px-4 py-2 font-medium">Last entry</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((row, i) => (
                  <tr key={i} className="border-b border-line last:border-0">
                    <td className="px-4 py-2">{row.organisation}</td>
                    <td className="px-4 py-2 text-inksoft">{row.form_title}</td>
                    <td className="px-4 py-2">{row.submitted_count}</td>
                    <td className="px-4 py-2 text-inksoft">{row.draft_count}</td>
                    <td className="px-4 py-2 text-inksoft">
                      {row.last_submitted_at
                        ? new Date(row.last_submitted_at).toLocaleDateString('en-NZ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}
